import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { TeamPricingDialog } from "@/components/TeamPricingDialog";
import api from "@/lib/api";
import {
  ArrowRight, Check, Layers, HeartHandshake, FileText, Compass,
  Wrench, Search, CalendarClock, Lock, Briefcase, Users, Map, Quote, Plus, Mail, Gift,
} from "lucide-react";

async function startCheckout(lookupKey, gift) {
  try {
    const payload = { lookup_key: lookupKey, origin_url: window.location.origin };
    if (gift && gift.gift_to_email) {
      payload.gift_to_email = gift.gift_to_email;
      payload.sender_name = gift.sender_name || "";
      payload.gift_note = gift.gift_note || "";
    }
    const { data } = await api.post("/payments/checkout", payload);
    window.location.href = data.checkout_url;
  } catch {
    toast.error("Couldn't start checkout. Redirecting you to sign up…");
    window.location.href = `/register?intent=${lookupKey}`;
  }
}

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55, ease: "easeOut", delay: d },
});

const PRICING = [
  {
    key: "part_1", name: "Part 1", full: "The Operating System — Foundations", price: 79,
    tone: "primary",
    desc: "The practical, template-heavy operating system for new managers.",
    points: ["Understand the role & the work", "Run the work: priorities, delegation, rhythm", "11 downloadable templates", "First 90-day plan"],
  },
  {
    key: "bundle", name: "Bundle", full: "All 3 toolkits", price: 170, tone: "coral", featured: true,
    desc: "The full system — structure and the human side, together.",
    points: ["Everything in Part 1", "Everything in Part 2", "Best value — save $25", "The complete manager toolkit"],
  },
  {
    key: "part_2", name: "Part 2", full: "The Human Side of Management", price: 79, tone: "plum",
    desc: "Trust, coaching, feedback, and the human systems behind great teams.",
    points: ["Trust & psychological safety", "Coaching & feedback that lands", "Difficult conversations", "Leading through change"],
  },
];

const FEATURES = [
  { icon: Search, title: "Find what you need, when you need it", body: "Search topics, scenarios, and templates without reading everything from beginning to end." },
  { icon: Compass, title: "Understand the why, not just the what", body: "Get the context and practical considerations to use your own judgment in your own environment." },
  { icon: FileText, title: "Put it into practice", body: "Use templates, scripts, worksheets, and reflection tools to turn guidance into action." },
  { icon: Wrench, title: "Make it your own", body: "Build a system that works for you, your team, and the realities of your organization." },
];

const BENEFITS = [
  { label: "Toolkit 1 · The Role", problem: "Your days are full of meetings, questions, competing priorities, and putting out fires, and you're struggling to find time to actually manage the work.", gift: "The Role — role clarity, core responsibilities, priorities, and a practical foundation for managing well.", outcome: "The ability to think more strategically about your role as a people manager.", tone: "primary" },
  { label: "Toolkit 2 · The People", problem: "You care about your team, but you're wondering whether you're doing enough to build trust, recognize contributions, support growth, and make everyone feel like they matter.", gift: "The People — support for 1:1s, trust, feedback, motivation, development, and performance.", outcome: "The ability to understand what each person on your team needs and support them well.", tone: "plum" },
  { label: "Toolkit 3 · The Moments", problem: "There's a conversation you're avoiding, a situation you're second-guessing, or something happening with your team that you're not quite sure how to handle.", gift: "The Moments — practical guidance and scripts for difficult conversations and real management scenarios.", outcome: "The ability to handle difficult conversations and real situations with more confidence and a clear next step.", tone: "gold" },
];

const TESTIMONIALS = [
  { quote: "This is single-handedly the best manager resource I have found. I've used it proactively to prepare for a situation but also for alignment across our management team on how to handle situations.", role: "Director, Customer Service" },
  { quote: "I've been looking for something that I can use across multiple departments with different team configurations and manager needs. The PDFs are really useful and easy to understand with simple to implement steps. Honestly, this discovery made my day/week/month.", role: "VP, Human Resources — retreat & conference services" },
  { quote: "Wow, this toolkit was the immediate resource to help me in tough situations. I didn't know how much I needed this!", role: "Manager, healthcare" },
  { quote: "I struggle in the emotional parts of management, and your support has helped me think through these situations more proactively.", role: "Manager, data analytics" },
  { quote: "My team is navigating uncertainty and unimaginable change. Your content has become a lifesaver for me as I keep morale up and make sure everyone has what they need.", role: "Manager, government services" },
  { quote: "I printed out a PDF for a mentoring session about managing people with more experience. We used it to guide our conversation, and the structure was a valuable add!", role: "Manager, marketing" },
  { quote: "My favorite part is the sample phrases to use in your conversations. It's not always easy to find the most intentional words in the moment, and this helps me prepare.", role: "Manager, Undisclosed Industry" },
  { quote: "The immediate access to context and a structure for how to approach different situations is so helpful. The framing also helped a situation feel less personal once I realized it was common.", role: "Manager, healthcare" },
];

const TOOLKITS = [
  {
    key: "role", tag: "Toolkit 1", name: "The Role", full: "Management 101", tone: "primary",
    icon: Briefcase, oneliner: "Clarity", price: 65, buyKey: "part_1",
    hook: "You're busy all day. But are you actually getting to the work of managing?",
    intro: "Your calendar is full, priorities keep shifting, and everyone needs something from you. You're responsible for keeping everything moving, but finding the time and space to actually manage the work can feel impossible.",
    action: "Get clearer on what the role requires, where to focus your attention, and how to establish the structure that helps you and your team operate more effectively.",
    previews: ["/previews/role-1.png", "/previews/role-2.png"],
    lead: "Management is a different job — with skills no one taught you.",
    body: "Your output is no longer your own; it's your team's. Toolkit 1 turns that daunting shift into a finite, repeatable set of practices you can actually hold — understanding the role, running the work, building the team, and making the most of your first 90 days.",
    includes: ["Role clarity checklist", "First 90 days plan", "Delegation templates", "Meeting rhythms"],
    points: ["Role clarity, expectations & manager responsibilities", "Priorities, delegation & decision-making", "1:1s, meetings & team operating rhythms", "Planning, performance & your own manager effectiveness", "Ready-to-use templates & worksheets for immediate application"],
    status: "A little sneak peek",
    outline: [
      { section: "Understand the Role", items: ["What Changes When You Become a Manager", "Core Responsibilities of Every Manager", "Management Self-Assessment"] },
      { section: "Understand the Team and Work", items: ["Understand the Team's Work", "Team Purpose and Mandate", "What the Team Owns / Does Not Own", "Workflow and Handoff Mapping", "Translate Goals Into Team Outcomes", "Team Success Measures"] },
      { section: "Run the Work", items: ["Clarify Roles and Decision Rights", "Prioritize Work", "Delegate Work", "Manage Workload and Capacity", "Establish an Operating Rhythm", "Track Results, Risks, and Decisions"] },
      { section: "Build and Maintain the Team", items: ["Plan for Hiring", "Onboard and Train Employees", "Manage Performance", "Develop Skills and Coverage"] },
      { section: "Work Across the Organization", items: ["Connect Work Across the Business", "Ask for Resources", "Escalate Constraints", "Communicate and Maintain Information Flow"] },
      { section: "Your First 90 Days", items: ["Make the Most of Your First 90 Days"] },
    ],
  },
  {
    key: "people", tag: "Toolkit 2", name: "The People", full: "The human side of managing people.", tone: "plum",
    icon: Users, oneliner: "Connection", price: 65, buyKey: "part_2",
    hook: "You care about your people. You just wish you had more time and tools to support them the way you'd like.",
    intro: "You notice when someone's disengaged, when they need encouragement, or when something feels off. You want your people to trust you, feel valued, and have opportunities to grow. But knowing those things matter and knowing how to consistently create them are two different things.",
    action: "Get practical guidance for building trust, understanding individual needs, giving meaningful feedback, supporting development, and creating the conditions for people to do their best work.",
    previews: ["/previews/people-1.png", "/previews/people-2.png"],
    lead: "You manage people — not just tasks.",
    body: "Toolkit 2 is the relational, human side of management: understanding your people, building trust, having the conversations that matter, and creating the conditions where people do their best work — without lowering the bar.",
    includes: ["Hiring scorecards", "Onboarding checklist", "1:1 agenda", "Feedback scripts"],
    points: ["Hiring, onboarding & setting people up for success", "Trust, communication & individual working needs", "Feedback, coaching & development", "Motivation, recognition, wellbeing & performance", "Ready-to-use templates & scripts for immediate application"],
    status: "A little sneak peek",
    outline: [
      { section: "Know Your People", items: ["You Cannot Manage What You Do Not Understand", "Learn Individual Preferences", "Understand Strengths, Aspirations, and Needs", "Adapt Without Becoming Inconsistent"] },
      { section: "Build Trust, Safety, and Mattering", items: ["Understand How Trust Is Built and Lost", "Build Psychological Safety Without Lowering the Bar", "Lead With Consistency, Fairness, and Predictability", "Help People Know They Matter"] },
      { section: "Connect Through Ongoing Conversations", items: ["One-on-Ones That Matter", "Listen to Understand", "Give Feedback Before It Becomes a Problem", "Coach Without Taking Over"] },
      { section: "Create the Conditions for Consistent Performance", items: ["Understand What People Need to Perform", "Motivation Is Individual", "Recognize and Reinforce Contribution", "Use Strengths Without Exploiting Them", "Support Growth Without Making Promises"] },
      { section: "Prepare for the Human Reality of Management", items: ["Difficult Moments Are Part of the Role", "Stay Grounded When Emotion Enters the Room", "Lead People Through Uncertainty and Change", "Know Your Role, Limits, and Support Channels", "Follow Through After the Moment"] },
    ],
  },
  {
    key: "moments", tag: "Toolkit 3", name: "The Moments", full: "The Scenario Guide", tone: "gold",
    icon: Map, oneliner: "Support", price: 65, buyKey: "part_3",
    hook: "Sometimes the hardest part of managing is knowing what to say or do next.",
    intro: "Someone's struggling. A conversation needs to happen. A team member is upset, performance is slipping, or something completely unexpected lands on your desk. You care about handling it well, but you don't always have the experience, language, or time to figure out the best approach.",
    action: "Get guidance for 45+ real management scenarios, including what to consider, what to do, what to avoid, and practical scripts to help you navigate the situation.",
    href: "https://toolkit.managemeantexcellence.com",
    previews: ["/previews/moments-1.png", "/previews/moments-2.png", "/previews/moments-3.png"],
    lead: "Real support for the moments no one prepared you for.",
    body: "Toolkit 3 is situation-by-situation guidance — with ready-to-use scripts — for the complicated, uncomfortable, and high-pressure moments that actually determine how management feels day to day.",
    includes: ["Performance conversation scripts", "Conflict playbooks", "Promotion & exit templates", "Hard-moment guidance"],
    points: ["Performance, accountability & difficult scenarios & conversations", "Conflict, emotions & sensitive situations", "Boundaries, promotions, resignations, exits & more (45+ scenarios)", "Practical scripts + what to do next"],
    status: "Already available",
    note: "and more coming…",
    outline: [
      { section: "Performance & Accountability", items: ["When you need to establish regular 1:1s", "When you need to address a performance issue", "When someone is underperforming but trying", "When you need to hold someone accountable", "When expectations keep being missed", "When you need to deliver hard feedback", "When someone becomes defensive", "When performance improves… then slips again"] },
      { section: "Emotional & Human Moments", items: ["When big life events happen on your team", "When someone is crying at work", "When team morale is low", "When someone seems disengaged", "When grief enters the workplace", "When personal conflict spills into work"] },
      { section: "Identity, Authority & Leadership Presence", items: ["Being effective vs. being liked", "When you are not empowered in your role", "When you feel like an imposter", "When you need to set boundaries", "When you manage someone who has more experience than you"] },
      { section: "Team Dynamics & Relationships", items: ["When you transition from peer to boss", "When you need to rebuild trust", "When one person dominates the team", "When someone feels left out", "When you inherit a team", "When your team doesn't trust leadership", "When there is gossip or tension", "When quiet teams aren't actually healthy", "When you have to manage passive-aggressive behavior", "When you manage someone you do not particularly like"] },
      { section: "Career & Transitions", items: ["When someone resigns", "When someone wants a promotion you cannot give", "When you are preparing someone for their next role", "When a new employee joins the team", "When someone returns from leave"] },
      { section: "Self-Management for Managers", items: ["When you feel lonely as a manager", "When you are burned out as a manager", "When you dread logging in", "When work starts affecting your personal life", "When you need to recover after hard conversations"] },
      { section: "Managing Up & Organizational Reality", items: ["When your team is resistant to change", "When you are navigating AI as a manager", "When you have to manage through ambiguity", "When leadership decisions don't fully make sense to you either", "When politics start affecting decisions", "When you are expected to do more with less", "When priorities keep changing"] },
    ],
  },
];

const TONE = {
  primary: { text: "text-nm-primary", bg: "bg-nm-primary", tint: "bg-nm-primary/10", ring: "border-nm-primary ring-nm-primary/30", soft: "bg-nm-primary/5 border-nm-primary/15" },
  plum: { text: "text-nm-plum", bg: "bg-nm-plum", tint: "bg-nm-plum/10", ring: "border-nm-plum ring-nm-plum/30", soft: "bg-nm-plum/5 border-nm-plum/15" },
  coral: { text: "text-nm-coral", bg: "bg-nm-coral", tint: "bg-nm-coral/10", ring: "border-nm-coral ring-nm-coral/30", soft: "bg-nm-coral/[0.06] border-nm-coral/15" },
  gold: { text: "text-[#B0851E]", bg: "bg-[#C2952B]", tint: "bg-[#C2952B]/12", ring: "border-[#C2952B] ring-[#C2952B]/30", soft: "bg-[#C2952B]/[0.09] border-[#C2952B]/20" },
};

function OutlineCard({ tk }) {
  const tc = TONE[tk.tone];
  const idx = TOOLKITS.findIndex((t) => t.key === tk.key);
  const Icon = tk.icon;
  return (
    <div className="rounded-2xl overflow-hidden border border-nm-ink/10 bg-[#F4F3EE] nm-shadow-md">
      {/* Header band */}
      <div className={`${tc.bg} px-6 sm:px-9 pt-7 pb-6 relative`} style={{ borderBottom: "3px solid #C2952B" }}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-11 w-11 rounded-full bg-white flex items-center justify-center shrink-0">
              <span className={`font-serif text-xl font-semibold ${tc.text}`}>{idx + 1}</span>
            </div>
            <div>
              <h3 className="font-serif text-3xl sm:text-4xl text-white leading-none">{tk.name}</h3>
              <p className="font-serif italic text-white/85 text-lg mt-1.5">{tk.oneliner}</p>
            </div>
          </div>
          <span className="hidden sm:inline-flex items-center rounded-full bg-white/15 border border-white/25 px-3.5 py-1.5 text-xs font-semibold text-white tracking-wide backdrop-blur-sm" data-testid="preview-status">
            {tk.status}
          </span>
        </div>
      </div>
      {/* Content */}
      <div className="px-5 sm:px-9 py-8">
        <div className="flex items-center gap-2.5 mb-6">
          <Icon size={20} className={tc.text} />
          <h4 className="font-serif text-2xl text-nm-ink/80 font-semibold">The Content</h4>
        </div>
        <div className="columns-1 md:columns-2 gap-5 [column-fill:balance]">
          {tk.outline.map((sec) => (
            <div key={sec.section} className="mb-5 break-inside-avoid rounded-xl bg-white border border-nm-ink/[0.06] p-5 nm-shadow-sm">
              <div className={`font-serif text-lg font-semibold leading-snug ${tc.text}`}>{sec.section}</div>
              <ol className="mt-3 space-y-1.5">
                {sec.items.map((it, k) => (
                  <li key={it} className="flex gap-2 text-[13.5px] leading-snug text-nm-ink/80">
                    <span className={`shrink-0 font-semibold ${tc.text} tabular-nums`}>{k + 1}.</span>
                    <span>{it}</span>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
        {tk.note && (
          <p className={`text-sm font-serif italic font-semibold mt-1 ${tc.text}`}>{tk.note}</p>
        )}
      </div>
    </div>
  );
}

function ToolkitPreview({ tk }) {
  const tc = TONE[tk.tone];
  // Slide 0 = native "The Content" outline, then the real screenshots.
  const slides = React.useMemo(
    () => ["outline", ...(tk.previews || [])],
    [tk.key]
  );
  const [n, setN] = React.useState(0);
  React.useEffect(() => {
    setN(0);
    const id = setInterval(() => setN((v) => (v + 1) % slides.length), 6000);
    return () => clearInterval(id);
  }, [tk.key, slides.length]);
  const labels = ["The Content", ...(tk.previews || []).map((_, i) => `Screen ${i + 1}`)];
  return (
    <div className="mx-auto max-w-4xl mt-10" data-testid="toolkit-preview">
      <motion.div
        key={`${tk.key}-${n}`}
        initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}>
        {slides[n] === "outline" ? (
          <OutlineCard tk={tk} />
        ) : (
          <div className={`rounded-2xl border ${tc.soft} p-2.5 sm:p-3 nm-shadow-md`}>
            <div className="relative rounded-xl overflow-hidden border border-nm-ink/10 bg-white" style={{ aspectRatio: "1440 / 900" }}>
              <img src={slides[n]} alt={`${tk.name} preview`} loading="lazy"
                className="absolute inset-0 h-full w-full object-cover object-top" data-testid="preview-image" />
            </div>
          </div>
        )}
      </motion.div>
      <div className="flex items-center justify-center gap-2 mt-4">
        {slides.map((_, i) => (
          <button key={i} onClick={() => setN(i)} aria-label={`Preview: ${labels[i]}`} data-testid={`preview-dot-${i}`}
            className={`h-2 rounded-full transition-all duration-300 ${i === n ? `w-7 ${tc.bg}` : "w-2 bg-nm-ink/20 hover:bg-nm-ink/40"}`} />
        ))}
      </div>
      <p className={`text-center text-sm font-medium mt-3 ${tc.text}`}>A look inside {tk.name}</p>
    </div>
  );
}

function ToolkitStack() {
  const [selected, setSelected] = React.useState("role");
  const cur = TOOLKITS.find((t) => t.key === selected) || TOOLKITS[0];
  return (
    <div>
      <p className="text-center font-serif text-xl sm:text-2xl text-nm-ink/90 leading-snug max-w-3xl mx-auto mb-8" data-testid="toolkit-bridge">
        If you were given responsibility for people without a clear picture of what good management actually requires, this was built for you.
      </p>
      <div className="flex justify-center" aria-hidden="true">
        <svg width="60" height="20" viewBox="0 0 60 20" className="text-nm-coral/70 mb-1">
          {[...Array(7)].map((_, k) => {
            const a = (Math.PI / 8) * (k - 3) - Math.PI / 2;
            return <line key={k} x1={30} y1={20} x2={30 + 17 * Math.cos(a)} y2={20 + 17 * Math.sin(a)} stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />;
          })}
        </svg>
      </div>
      <div className="flex justify-center">
        <div className="inline-flex items-center gap-4 sm:gap-5 rounded-full bg-nm-ink text-white text-base sm:text-xl font-semibold pl-6 pr-8 py-5 tracking-wide nm-shadow-md" data-testid="toolkit-system-badge">
          <Layers size={26} className="text-nm-coral shrink-0" />
          <span className="h-7 w-px bg-white/25" />
          <span>Three toolkits. One complete management operating system.</span>
        </div>
      </div>
      <div className="mx-auto max-w-3xl mt-8">
        {TOOLKITS.map((t, idx) => {
          const tc = TONE[t.tone];
          const Icon = t.icon;
          const sel = t.key === selected;
          const delay = (TOOLKITS.length - 1 - idx) * 0.16;
          const AccessInner = (<>Buy <ArrowRight size={16} /></>);
          const btnCls = `inline-flex items-center gap-2 rounded-full ${tc.bg} px-6 py-2.5 text-sm font-semibold text-white hover:brightness-105 hover:-translate-y-0.5 transition-[filter,transform] duration-200`;
          return (
            <React.Fragment key={t.key}>
              {idx > 0 && (
                <div className="relative flex justify-center h-10" aria-hidden="true">
                  <div className="absolute top-0 h-full w-px bg-nm-ink/15" />
                  <div className="absolute top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-white border border-nm-ink/12 flex items-center justify-center nm-shadow-sm">
                    <Plus size={16} className="text-nm-ink/55" />
                  </div>
                </div>
              )}
              <motion.div
                initial={{ y: 40 }} animate={{ y: 0 }}
                transition={{ delay, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                onClick={() => setSelected(t.key)}
                role="button" tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected(t.key); } }}
                data-testid={`toolkit-card-${t.key}`}
                className={`relative rounded-3xl border p-6 sm:p-8 ${tc.soft} nm-shadow-sm cursor-pointer transition-[box-shadow,transform] duration-200 hover:-translate-y-0.5 ${sel ? `ring-2 ring-offset-2 ${tc.ring}` : ""}`}
              >
                <div className={`absolute top-6 right-6 h-11 w-11 rounded-xl ${tc.tint} ${tc.text} flex items-center justify-center`}><Icon size={22} /></div>
                <div className="flex flex-col sm:flex-row sm:items-start gap-5 sm:gap-7">
                  <div className="shrink-0">
                    <div className={`h-14 w-14 rounded-full ${tc.bg} text-white font-serif text-2xl flex items-center justify-center nm-shadow-sm`}>{idx + 1}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-[12px] font-semibold uppercase tracking-[0.18em] ${tc.text}`}>{t.tag}</div>
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mt-1">
                      <h3 className="font-serif text-3xl sm:text-4xl text-nm-ink leading-none">{t.name}</h3>
                      <span className={`font-serif italic text-xl ${tc.text}`}>{t.oneliner}</span>
                    </div>
                    <p className="text-[15px] font-semibold text-nm-ink mt-3 leading-snug">{t.hook}</p>
                    <p className="text-sm text-nm-neutral mt-2 leading-relaxed">{t.intro}</p>
                    <p className="text-[15px] text-nm-ink/85 mt-2 leading-relaxed">{t.action}</p>
                    <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mt-5" data-testid={`toolkit-points-${t.key}`}>
                      {t.points.map((pt) => (
                        <li key={pt} className="flex items-start gap-2.5 text-[15px] leading-snug text-nm-ink/85">
                          <Check size={16} className={`shrink-0 mt-0.5 ${tc.text}`} /> {pt}
                        </li>
                      ))}
                    </ul>
                    <div className="mt-6 flex items-center gap-4">
                      <button type="button" onClick={(e) => { e.stopPropagation(); startCheckout(t.buyKey); }} data-testid={`toolkit-buy-${t.key}`} className={btnCls}>{AccessInner}</button>
                      <span className={`text-sm font-medium ${sel ? tc.text : "text-nm-neutral"}`}>{sel ? "Previewing below ↓" : "Tap to preview"}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            </React.Fragment>
          );
        })}
      </div>
      <ToolkitPreview tk={cur} />
    </div>
  );
}

function QuotesMarquee() {
  const row = [...TESTIMONIALS, ...TESTIMONIALS];
  return (
    <section className="py-20 overflow-hidden" data-testid="quotes-section">
      <div className="mx-auto max-w-6xl px-6 text-center">
        <div className="text-sm font-semibold uppercase tracking-[0.22em] text-nm-coral">In their words</div>
        <h2 className="font-serif text-3xl sm:text-4xl text-nm-ink mt-3">Managers already lead differently with this</h2>
        <p className="text-nm-neutral mt-3 max-w-2xl mx-auto">Real notes from managers using the Manage(Meant) Excellence toolkits.</p>
      </div>
      <div className="relative mt-12 [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]">
        <div className="flex gap-6 w-max animate-[marquee_60s_linear_infinite] hover:[animation-play-state:paused]">
          {row.map((q, i) => (
            <figure key={i} className="w-[360px] shrink-0 rounded-2xl border border-nm-primary/12 bg-nm-surface p-6 nm-shadow-sm" data-testid="quote-card">
              <Quote size={22} className="text-nm-coral/50" />
              <blockquote className="text-[15px] leading-loose text-nm-ink/85 mt-3">{q.quote}</blockquote>
              <figcaption className="text-sm font-medium text-nm-primary mt-4">— {q.role}</figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

const SITE = "https://www.managemeantexcellence.com";
const NAV_LINKS = [
  { label: "Management, Honestly", href: `${SITE}/resources` },
  { label: "Resources for Managers", href: `${SITE}/offers` },
  { label: "Live Trainings", href: `${SITE}/live` },
  { label: "For Teams", href: `${SITE}/training` },
  { label: "About", href: `${SITE}/about` },
  { label: "Contact", href: `${SITE}/contact` },
];

function Nav() {
  return (
    <header className="sticky top-0 z-40 border-b border-nm-primary/8 bg-nm-bg/85 backdrop-blur-xl">
      <div className="mx-auto max-w-7xl flex items-center justify-between gap-4 px-6 h-16">
        <a href={SITE} className="leading-none shrink-0" data-testid="landing-logo">
          <span className="font-serif text-2xl tracking-tight">
            <span className="text-nm-ink">Manage</span><span className="text-nm-primary">(Meant)</span>
          </span>
          <span className="ml-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-nm-neutral align-middle">Excellence</span>
        </a>
        <nav className="hidden lg:flex items-center gap-6" data-testid="site-nav">
          {NAV_LINKS.map((l) => (
            <a key={l.label} href={l.href} data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
              className="text-[13.5px] text-nm-neutral hover:text-nm-primary transition-colors whitespace-nowrap">{l.label}</a>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          <Link to="/login" data-testid="nav-login" className="hidden sm:inline text-sm text-nm-neutral hover:text-nm-ink transition-colors">Sign in</Link>
          <Link to="/register" data-testid="nav-get-started" className="rounded-full bg-nm-coral px-5 py-2.5 text-sm font-medium text-white hover:brightness-105 hover:-translate-y-0.5 transition-[filter,transform] duration-200 whitespace-nowrap">Get started</Link>
        </div>
      </div>
    </header>
  );
}

function PricingCard({ b, giftActive }) {
  const bc = TONE[b.tone];
  const [giftOn, setGiftOn] = React.useState(false);
  const [gEmail, setGEmail] = React.useState("");
  const [gName, setGName] = React.useState("");
  const [gNote, setGNote] = React.useState("");
  const [teamOpen, setTeamOpen] = React.useState(false);
  const buy = () => {
    if (giftOn) {
      const email = gEmail.trim();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        toast.error("Enter a valid colleague email to send a free gift.");
        return;
      }
      startCheckout(b.intent, { gift_to_email: email, sender_name: gName.trim(), gift_note: gNote.trim() });
    } else {
      startCheckout(b.intent);
    }
  };
  return (
    <div className={`relative rounded-2xl p-6 flex flex-col ${b.featured ? `border-2 ${bc.ring.split(" ")[0]} ${bc.soft.split(" ")[0]}` : `border ${bc.soft}`}`}>
      {b.featured && <span className={`absolute -top-3 left-6 rounded-full ${bc.bg} px-3 py-1 text-[11px] font-semibold text-white`}>Best value</span>}
      <div className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${bc.text}`}>{b.name}</div>
      <div className="font-serif text-xl text-nm-ink mt-1">{b.full}</div>
      <div className="mt-2 flex items-baseline gap-1">
        <span className="font-serif text-3xl text-nm-ink">{b.price}</span>
        <span className="text-xs text-nm-neutral">{b.featured ? "one-time" : "each"}</span>
      </div>
      <p className="text-sm text-nm-neutral mt-2 flex-1">{b.desc}</p>
      {b.featured && (
        <>
          <button type="button" onClick={() => setTeamOpen(true)} data-testid="team-pricing-link"
            className="mt-2 self-start text-[12px] font-medium text-nm-primary underline underline-offset-2 decoration-nm-primary/40 hover:decoration-nm-primary transition-colors">
            Buying for your team? See team pricing
          </button>
          <TeamPricingDialog open={teamOpen} onOpenChange={setTeamOpen} onBuy={(k) => startCheckout(k)} />
        </>
      )}
      {b.note && <p className={`text-[12px] font-medium mt-2 ${bc.text}`}>{b.note}</p>}
      <button type="button" onClick={buy} data-testid={b.testid}
        className={`mt-5 inline-flex items-center justify-center gap-2 rounded-full ${bc.bg} px-5 py-3 text-sm font-semibold text-white hover:brightness-105 hover:-translate-y-0.5 transition-[filter,transform] duration-200`}>
        {b.featured ? "Buy all three" : `Buy ${b.name}`} <ArrowRight size={15} />
      </button>
      {giftActive && (
        <div className="mt-4 border-t border-nm-ink/[0.07] pt-4">
          <label className="flex items-start gap-2 cursor-pointer select-none" data-testid={`gift-toggle-${b.intent}`}>
            <input type="checkbox" checked={giftOn} onChange={(e) => setGiftOn(e.target.checked)}
              data-testid={`gift-checkbox-${b.intent}`} className="mt-0.5 h-4 w-4 accent-nm-coral shrink-0" />
            <span className="text-[13px] font-medium text-nm-ink leading-snug">
              <Gift size={13} className="inline mr-1 -mt-0.5 text-nm-coral" />Send one free to a colleague
            </span>
          </label>
          {giftOn && (
            <div className="mt-3 space-y-2" data-testid={`gift-form-${b.intent}`}>
              <input type="email" value={gEmail} onChange={(e) => setGEmail(e.target.value)}
                placeholder="Colleague's email *" data-testid={`gift-email-${b.intent}`}
                className="w-full rounded-lg border border-nm-ink/15 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-nm-coral/30" />
              <input type="text" value={gName} onChange={(e) => setGName(e.target.value)}
                placeholder="Your name (optional)" data-testid={`gift-name-${b.intent}`}
                className="w-full rounded-lg border border-nm-ink/15 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-nm-coral/30" />
              <textarea value={gNote} onChange={(e) => setGNote(e.target.value)} rows={2}
                placeholder="A short note (optional)" data-testid={`gift-note-${b.intent}`}
                className="w-full rounded-lg border border-nm-ink/15 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-nm-coral/30" />
              <p className="text-[11px] text-nm-neutral leading-snug">They'll receive the same toolkit free, delivered by email right after your purchase.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Landing() {
  const [promo, setPromo] = React.useState({ active: false, endsAt: null });
  React.useEffect(() => {
    api.post("/track", { type: "landing_view" }).catch(() => {});
    api.get("/config")
      .then(({ data }) => setPromo({ active: !!data.gift_offer_active, endsAt: data.gift_offer_ends_at }))
      .catch(() => {});
  }, []);
  return (
    <div className="min-h-screen bg-nm-bg nm-grain font-sans text-nm-ink">
      <Nav />

      {/* Hero — the 3-toolkit system is the star */}
      <section className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 left-[15%] h-72 w-72 rounded-full bg-nm-primary/10 blur-3xl" />
          <div className="absolute top-8 right-[15%] h-64 w-64 rounded-full bg-nm-coral/10 blur-3xl" />
          <div className="absolute bottom-0 left-1/3 h-72 w-72 rounded-full bg-nm-plum/[0.08] blur-3xl" />
        </div>
        <div className="relative mx-auto max-w-6xl px-6 pt-10 pb-16">
          <motion.div {...fade()} className="px-2 sm:px-4 pt-8 pb-6">
            <div className="text-center max-w-3xl mx-auto">
              <div className="flex items-center justify-center gap-4">
                <span className="h-px w-10 sm:w-16 bg-nm-coral/40" />
                <div className="text-sm sm:text-[15px] font-semibold uppercase tracking-[0.22em] text-nm-coral">The Manager Operating System</div>
                <span className="h-px w-10 sm:w-16 bg-nm-coral/40" />
              </div>
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-nm-ink mt-4">
                Managing people is a lot. <span className="text-nm-primary">Figuring out how to do it shouldn't be another full-time job.</span>
              </h1>
              <p className="text-base sm:text-lg text-nm-neutral leading-relaxed mt-6 max-w-2xl mx-auto">
                You're responsible for getting the work done, supporting the people doing it, and navigating everything that comes up along the way. Some days, it's hard to know where to focus, what to say, or whether you're approaching any of it the right way.
              </p>
            </div>

            <div id="toolkits" className="mt-12 scroll-mt-24">
              <ToolkitStack />
            </div>
          </motion.div>

          <div id="pricing" className="mt-10 scroll-mt-24">
            {promo.active && (
              <div className="mb-8 flex justify-center" data-testid="gift-promo-banner">
                <div className="inline-flex items-center gap-3 rounded-full border border-nm-coral/25 bg-nm-coral/[0.07] px-5 py-2.5 text-sm text-nm-ink max-w-xl">
                  <Gift size={16} className="text-nm-coral shrink-0" />
                  <span className="leading-snug"><span className="font-semibold text-nm-coral">Limited time:</span> Buy one, send one free — gift any toolkit to a colleague at no extra cost.</span>
                </div>
              </div>
            )}
            <div className="text-center max-w-2xl mx-auto">
              <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-nm-coral">Ready to buy</div>
              <h2 className="font-serif text-2xl sm:text-3xl text-nm-ink mt-2">Buy one toolkit, or the whole system.</h2>
              <p className="text-sm font-semibold text-nm-ink mt-5">$65 per toolkit&nbsp; |&nbsp; $170 for all three&nbsp; |&nbsp; One-time purchase</p>
            </div>
            <div className="mt-9 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
              {[
                { key: "role", tone: "primary", name: "Toolkit 1", full: "The Role", price: "$65", desc: "Understand the job and run the work.", intent: "part_1", testid: "pricing-part_1" },
                { key: "people", tone: "plum", name: "Toolkit 2", full: "The People", price: "$65", desc: "Lead people intentionally.", intent: "part_2", testid: "pricing-part_2" },
                { key: "moments", tone: "gold", name: "Toolkit 3", full: "The Moments", price: "$65", desc: "Handle real situations, with scripts.", intent: "part_3", testid: "pricing-part_3", note: "Unlocks the Scenario Guide" },
                { key: "bundle", tone: "coral", name: "All 3 toolkits", full: "The full system", price: "$170", desc: "The complete operating system — save $25.", intent: "bundle", testid: "pricing-bundle", featured: true },
              ].map((b) => (
                <PricingCard key={b.key} b={b} giftActive={promo.active} />
              ))}
            </div>
            <p className="text-[13px] text-nm-neutral mt-6 text-center">Instant access to all templates and scripts.</p>
            <p className="text-[13px] text-nm-neutral mt-2 text-center">Have a professional development budget? We've even <a href="/budget-request-email.txt" download="budget-request-email.txt" data-testid="budget-download-link" className="font-medium text-nm-primary underline underline-offset-2 decoration-nm-primary/40 hover:decoration-nm-primary transition-colors">written a budget request email</a> you can send to your manager.</p>
          </div>
        </div>
      </section>

      {/* Testimonials — real quotes, auto-scrolling */}
      <div className="border-y border-nm-primary/8 bg-white/50">
        <QuotesMarquee />
      </div>

      {/* Benefit grid — every part of management has a toolkit */}
      <section className="mx-auto max-w-6xl px-6 py-16">
        <motion.div {...fade()} className="max-w-2xl">
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-nm-coral">Every part of management has a toolkit</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-nm-ink mt-4 leading-tight">
            Start with whatever's hardest right now.
          </h2>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-5 mt-12">
          {BENEFITS.map((b, i) => {
            const bc = TONE[b.tone];
            return (
              <motion.div key={b.problem} {...fade(i * 0.08)}
                className="flex flex-col rounded-2xl border border-nm-primary/10 bg-nm-surface p-7 nm-shadow-sm"
                data-testid={`benefit-${i}`}>
                <div className={`text-xs font-semibold tracking-[0.12em] ${bc.text}`} data-testid={`benefit-label-${i}`}>{b.label}</div>
                <div className="mt-4 text-[11px] font-semibold uppercase tracking-[0.16em] text-nm-neutral/70">Start here if</div>
                <p className="text-nm-ink font-medium mt-2 leading-snug">{b.problem}</p>
                <div className={`mt-5 rounded-xl p-4 ${bc.soft} border`}>
                  <div className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${bc.text}`}>What you get</div>
                  <p className="text-nm-ink/85 mt-1.5 text-sm leading-relaxed">{b.gift}</p>
                </div>
                <div className="mt-5 flex items-start gap-2 text-nm-ink/85">
                  <Check size={17} className={`shrink-0 mt-0.5 ${bc.text}`} />
                  <span className="leading-snug"><span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-nm-neutral/70 block">Outcome</span>{b.outcome}</span>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* Why it works */}
      <section className="border-y border-nm-primary/8 bg-white/50">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <motion.div {...fade()} className="max-w-3xl">
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-nm-coral">Why it works</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-nm-ink mt-4 leading-tight">
              You don't need another thing to keep up with. You need something you can actually use.
            </h2>
            <p className="text-base text-nm-neutral mt-5 leading-relaxed">Most managers aren't sitting around wondering what new management theory they should study next. They're trying to prepare for tomorrow's difficult conversation, figure out how to prioritize an overloaded team, or find a better way to support an employee who's struggling. The Manager Operating System is built for the work you're already doing.</p>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-12">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <motion.div key={f.title} {...fade(i * 0.08)} className="rounded-2xl border border-nm-primary/10 bg-nm-surface p-6 nm-shadow-sm">
                  <div className="h-11 w-11 rounded-xl bg-nm-primary/10 text-nm-primary flex items-center justify-center"><Icon size={20} /></div>
                  <h3 className="font-serif text-xl text-nm-ink mt-4">{f.title}</h3>
                  <p className="text-sm text-nm-neutral leading-relaxed mt-2">{f.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-6xl px-6 pb-24">
        <motion.div {...fade()} className="relative overflow-hidden rounded-[2rem] bg-nm-primary p-10 sm:p-16 text-white text-center">
          <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-nm-coral/25 blur-3xl" />
          <div className="absolute -left-16 bottom-0 h-56 w-56 rounded-full bg-nm-highlight/20 blur-3xl" />
          <div className="relative">
            <h2 className="font-serif text-4xl sm:text-5xl leading-tight max-w-2xl mx-auto">Clarity for the role. Support for the people. Guidance for the moments.</h2>
            <Link to="/register" data-testid="cta-get-started" className="mt-8 inline-flex items-center gap-2 rounded-full bg-nm-coral px-8 py-4 text-sm font-medium hover:brightness-105 hover:-translate-y-0.5 transition-[filter,transform] duration-200">
              Start now <ArrowRight size={16} />
            </Link>
          </div>
        </motion.div>

        <div className="mx-auto max-w-3xl mt-16" data-testid="faq">
          <div className="text-center">
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-nm-coral">Questions</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-nm-ink mt-2">Frequently asked</h2>
          </div>
          <div className="mt-8 space-y-3">
            {[
              { q: "Is this a course?", a: "No. This is a practical, on-demand toolkit designed to support you while you're actually managing. Use the digital library whenever you need guidance in the moment." },
              { q: "What if I already have a coach or HR partner?", a: "These toolkits do not replace either. It gives you the language to walk into those conversations more clearly, and to know when to bring HR in." },
              { q: "How do I access it after I buy?", a: "We email you a magic link. Click it, and your library opens. The link works on any device. We'll re-send it whenever you ask." },
              { q: "Can I purchase multiple licenses for managers within my business?", a: "Yes — please email hello@managemeantexcellence.com to get the conversation started." },
              { q: "Is there a refund policy?", a: "If the kit doesn't help, reply to your purchase email within 14 days. We'll refund you in full, no scripts required." },
              { q: "Will more scenarios or updates be made?", a: "Yes — and you'll get them automatically. The library grows as managers ask for the situations they wish were in there." },
            ].map((f, i) => (
              <details key={i} className="group rounded-2xl border border-nm-primary/12 bg-nm-surface px-6 py-4 nm-shadow-sm" data-testid={`faq-item-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer list-none font-serif text-lg text-nm-ink">
                  {f.q}
                  <Plus size={18} className="text-nm-coral shrink-0 transition-transform duration-200 group-open:rotate-45" />
                </summary>
                <p className="text-nm-neutral leading-relaxed mt-3">{f.a}</p>
              </details>
            ))}
          </div>
        </div>

        <p className="text-center text-sm text-nm-neutral mt-6" data-testid="not-ready-callout">
          Not quite ready?{" "}
          <a href="https://www.managemeantexcellence.com" target="_blank" rel="noopener noreferrer"
            data-testid="manager-support-link" className="font-medium text-nm-primary underline underline-offset-4 decoration-nm-primary/40 hover:decoration-nm-primary transition-colors duration-200">
            Head back to the manager support page
          </a>
        </p>
        <div className="mx-auto max-w-xl mt-8 rounded-2xl border border-nm-primary/12 bg-nm-surface px-6 py-5 text-center nm-shadow-sm" data-testid="contact-callout">
          <p className="text-sm text-nm-ink/80">
            Questions, or want to bring this to your organization?
          </p>
          <a href="mailto:hello@managemeantexcellence.com" data-testid="contact-email-link"
            className="mt-1.5 inline-flex items-center gap-2 font-medium text-nm-primary hover:text-nm-coral transition-colors duration-200">
            <Mail size={16} /> hello@managemeantexcellence.com
          </a>
        </div>
      </section>

      <footer className="border-t border-nm-primary/8 py-8">
        <div className="mx-auto max-w-6xl px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-nm-neutral">
          <span>© {new Date().getFullYear()} Manage(Meant) Excellence</span>
          <a href="/terms" data-testid="footer-terms-link" className="hover:text-nm-primary transition-colors duration-200">Terms &amp; Privacy</a>
          <span className="font-serif text-nm-primary">The Manager Operating System</span>
        </div>
      </footer>
    </div>
  );
}
